import { z } from 'zod';

// Job Status enum
export const JOB_STATUSES = [
  { value: 'accepting_candidates', label: 'Accepting Candidates' },
  { value: 'covered', label: 'Covered' },
  { value: 'interviewing', label: 'Interviewing' },
  { value: 'offer_out', label: 'Offer Extended' },
  { value: 'filled', label: 'Filled' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'lost', label: 'Lost' },
  { value: 'declined', label: 'Declined' }
];

// Employment Type enum
export const EMPLOYMENT_TYPES = [
  { value: 'Full-Time', label: 'Full-Time' },
  { value: 'Part-Time', label: 'Part-Time' },
  { value: 'Contract', label: 'Contract' },
  { value: 'Internship', label: 'Internship' }
];

// Experience Level enum
export const EXPERIENCE_LEVELS = [
  { value: 'Entry Level', label: 'Entry Level' },
  { value: 'Mid Level', label: 'Mid Level' },
  { value: 'Senior', label: 'Senior' },
  { value: 'Lead', label: 'Lead' },
  { value: 'Executive', label: 'Executive' }
];

// Location Requirements enum
export const LOCATION_REQUIREMENTS = [
  { value: 'No Preference', label: 'No Preference' },
  { value: 'On-Site', label: 'On-Site' },
  { value: 'Remote', label: 'Remote' },
  { value: 'Hybrid', label: 'Hybrid' }
];

// Currency enum
export const CURRENCIES = [
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'GBP', label: 'GBP' }
];

// Travel Required enum
export const TRAVEL_OPTIONS = [
  { value: '0%', label: '0%' },
  { value: '25%', label: '25%' },
  { value: '50%', label: '50%' },
  { value: '75%', label: '75%' },
  { value: '100%', label: '100%' }
];

// Yes/No options
export const YES_NO_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' }
];

// Zod validation schema
export const jobSchema = z.object({
  // Basic Information
  client: z.string().min(1, 'Client company is required'),
  client_cost_center: z.string().min(1, 'Client cost center is required'),
  workday_supervisory_org: z.string().min(1, 'Workday supervisory organization is required'),
  title: z.string().min(3, 'Job title must be at least 3 characters'),
  department: z.string().min(1, 'Department is required'),
  location: z.string().min(1, 'Location is required'),
  employment_type: z.string().min(1, 'Employment type is required'),
  location_requirements: z.string().min(1, 'Location requirements is required'),
  
  // Hiring Team
  hiring_manager: z.string().min(1, 'Hiring manager is required'),
  account_manager: z.string().min(1, 'Account manager is required'),
  
  // Job Details
  experience_level: z.string().min(1, 'Experience level is required'),
  required_skills: z.array(z.string()).min(1, 'At least one skill is required'),
  description: z.string().min(50, 'Job description must be at least 50 characters'),
  responsibilities: z.string().optional(),
  qualifications: z.string().optional(),
  benefits: z.string().optional(),
  
  // Compensation
  currency: z.string().default('USD'),
  salary_min: z.number().optional().nullable(),
  salary_max: z.number().optional().nullable(),
  
  // Logistics
  no_of_openings: z.number().min(1, 'Must have at least 1 opening').default(1),
  status: z.string().default('accepting_candidates'),
  travel: z.string().default('0%'),
  visa_sponsorship: z.string().default('Yes'),
  relocation_allowed: z.string().default('Yes')
}).refine(
  (data) => {
    // Custom validation: salary_max must be >= salary_min if both exist
    if (data.salary_min && data.salary_max) {
      return data.salary_max >= data.salary_min;
    }
    return true;
  },
  {
    message: 'Maximum salary must be greater than or equal to minimum salary',
    path: ['salary_max']
  }
);

export const defaultValues = {
  client: '',
  client_cost_center: '',
  workday_supervisory_org: '',
  title: '',
  department: '',
  location: '',
  employment_type: 'Full-Time',
  location_requirements: 'No Preference',
  hiring_manager: '',
  account_manager: '',
  experience_level: '',
  required_skills: [],
  description: '',
  responsibilities: '',
  qualifications: '',
  benefits: '',
  currency: 'USD',
  salary_min: null,
  salary_max: null,
  no_of_openings: 1,
  status: 'accepting_candidates',
  travel: '0%',
  visa_sponsorship: 'Yes',
  relocation_allowed: 'Yes'
};
